import { useFormState, useWatch } from "react-hook-form";
import PropTypes from "prop-types";

import Dropdown from "shared/components/inputs/form-fields/Dropdown";
import VStack from "shared/components/layout/stack/VStack";
import Form from "shared/components/inputs/form/Form";
import { useAccessGroupMembersApiDepartments } from "features/admin/access-groups/single/useAccessGroupMembers";
import ModalActions from "shared/components/feedback/modals/ModalActions";

import { sendNotification } from "shared/components/feedback/notification/notification";
import FormFeedbackNotificationText from "shared/components/feedback/error/FormFeedbackNotificationText";

import useClientContactLeads, {
    useAllUsers,
} from "features/admin/clients/single/useClientContactLeads";
import { getDropdownOptions } from "features/admin/offices/OfficesForm";

const ModalCTA = ({
    isEditing,
    isLoading,
    onClose,
    handleOnClickDeleteContactLead,
}) => {
    const { isValid } = useFormState();
    const formData = useWatch();
    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                label: isEditing ? "Save" : "Add",
                disabled: !isValid,
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            {...(isEditing && {
                tertiaryAction: {
                    type: "button",
                    leadingIcon: "delete",
                    label: "Delete",
                    onClick: () => handleOnClickDeleteContactLead(formData),
                },
            })}
            as="fieldset"
            disabled={isLoading}
        />
    );
};

function transformContactLeadDataToFormData(
    contactLeadData = {},
    departmentId = ""
) {
    return {
        name: contactLeadData.userId ?? "",
        department: departmentId,
    };
}

function transformFormDataToContactLeadData(
    formData = {},
    contactLeadData = {},
    departmentName = "",
    userData = null
) {
    const newContactLeadData = { ...contactLeadData };
    newContactLeadData.userId = formData.name;
    newContactLeadData.currentDepartmentId = formData.department;
    newContactLeadData.currentDepartment = departmentName;
    newContactLeadData.fullName = userData?.fullName;
    newContactLeadData.email = userData?.email;
    newContactLeadData.currentTitle = userData?.currentTitle ?? null;

    return newContactLeadData;
}

const ClientContactLeadsForm = ({ contactLeadIdToEdit, onClose }) => {
    const {
        clientContactLeads,
        createClientContactLead,
        updateClientContactLead,
        deleteClientContactLead,
    } = useClientContactLeads();

    const { allUsers } = useAllUsers();

    const { departments } = useAccessGroupMembersApiDepartments();

    if (clientContactLeads.isLoading) {
        return <div>Loading...</div>;
    }

    if (clientContactLeads.error) {
        return <div>Error</div>;
    }

    const departmentOptions = departments.isLoading
        ? []
        : getDropdownOptions(departments?.data);

    const contactLeadOptions = allUsers.isLoading
        ? []
        : getDropdownOptions(allUsers?.data, "id", "fullName");

    const isEditing = contactLeadIdToEdit !== undefined;
    const modifier = isEditing
        ? updateClientContactLead
        : createClientContactLead;

    const currentClientContactLeadData = clientContactLeads?.data.find(
        (ContactLead) => ContactLead.userId === contactLeadIdToEdit
    );

    if (isEditing && !currentClientContactLeadData) {
        return <div>Could not find contact lead</div>;
    }

    const handleOnClickDeleteContactLead = async (prevData) => {
        const response = await deleteClientContactLead.mutateAsync({ clientContactLeadId: contactLeadIdToEdit });
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isDeleting={true}
                name={currentClientContactLeadData.fullName}
            />,
            {
                close: true,
                action: true,
                actionProps: {
                    label: "Undo",
                    onClick: () => onSubmit(prevData),
                },
            }
        );
    };

    const onSubmit = async (formData) => {
        const clientContactLeadsData = transformFormDataToContactLeadData(
            formData,
            currentClientContactLeadData,
            departmentOptions.find((f) => f.value === formData.department)
                ?.label,
            allUsers?.data?.find((f) => f.id === formData.name)
        );

        const allData = {
            body: clientContactLeadsData,
        };

        const response = await modifier.mutateAsync(allData);
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isEditing={isEditing}
                name={clientContactLeadsData.fullName}
            />
        );
    };

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={transformContactLeadDataToFormData(
                currentClientContactLeadData,
                departmentOptions.find(
                    (f) => f.label === currentClientContactLeadData?.department
                )?.value
            )}
            mode="onChange"
        >
            <VStack spacing="Three">
                <VStack spacing="Two">
                    <Dropdown
                        name="department"
                        validations={{
                            required: true,
                        }}
                        label="Department"
                        options={departmentOptions}
                        itemsAreLoading={departments.isLoading}
                    />
                    <Dropdown
                        name="name"
                        validations={{
                            required: true,
                        }}
                        label="Name"
                        itemsAreLoading={allUsers.isLoading}
                        disabledOptionsList={
                            clientContactLeads.isLoading
                                ? []
                                : clientContactLeads.data.map((data) => ({
                                      value: data.userId,
                                  }))
                        }
                        disabledOptionTooltipText="Already in this Contact Lead"
                        options={contactLeadOptions}
                    />
                </VStack>
                <ModalCTA
                    isEditing={isEditing}
                    handleOnClickDeleteContactLead={
                        handleOnClickDeleteContactLead
                    }
                    onClose={onClose}
                    isLoading={modifier.isLoading}
                />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    isEditing: PropTypes.bool,
    contactLeadIdToEdit: PropTypes.number,
    isLoading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    handleOnClickDeleteContactLead: PropTypes.func
};

ClientContactLeadsForm.propTypes = {
    contactLeadIdToEdit: PropTypes.number,
    onClose: PropTypes.func.isRequired
};

export default ClientContactLeadsForm;
